/**
 * "Where does <City> want test reports emailed?" — asked once, on a job's
 * closeout, for a paper/email city with no filing email saved (Corbin 10/17).
 * Saving puts the address on the city, so every later job uses it and this
 * job's filing lands in the City Filing ready tray. "They don't take email
 * (mail it)" marks the city mail-only so it stops asking. Never blocks the
 * packet: closing it just leaves the city filing in "Fix first".
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Mail } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";

interface Prompt {
    show: boolean;
    providerId: string | null;
    cityName: string | null;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function CityEmailPrompt({ jobId }: { jobId: string }) {
    const qc = useQueryClient();
    const q = useQuery({
        queryKey: ["city-filings", "email-prompt", jobId],
        queryFn: () => api.get<Prompt>(`/api/city-filings/email-prompt/${jobId}`),
        retry: false,
    });
    const [email, setEmail] = useState("");
    const save = useMutation({
        mutationFn: (body: { email: string } | { mailOnly: true }) =>
            api.post(`/api/city-filings/providers/${q.data!.providerId}/filing-email`, { ...body, jobId }),
        onSuccess: (_r, body) => {
            toast.success(
                "mailOnly" in body
                    ? `${q.data?.cityName ?? "This city"} is set to mail. We won't ask again.`
                    : `Saved. ${q.data?.cityName ?? "This city"}'s reports go to ${email.trim()} from now on.`,
            );
            void qc.invalidateQueries({ queryKey: ["city-filings"] });
            void qc.invalidateQueries({ queryKey: ["close-out-documents", jobId] });
        },
        onError: (e) =>
            toast.error(
                e instanceof ApiError && e.body && typeof e.body === "object" && "message" in e.body
                    ? String((e.body as { message: unknown }).message)
                    : "Could not save",
            ),
    });
    const p = q.data;
    if (!p?.show || !p.providerId || save.isSuccess) return null;
    const valid = EMAIL_RE.test(email.trim());
    return (
        <div
            className="flex flex-col gap-2 rounded-xl border-2 border-amber-500 bg-amber-50 p-3 text-amber-950 dark:bg-amber-950/30 dark:text-amber-100"
            data-testid="city-email-prompt"
        >
            <p className="flex items-center gap-2 font-bold">
                <Mail className="h-4 w-4 flex-none" aria-hidden />
                Where does {p.cityName} want test reports emailed?
            </p>
            <p className="text-sm">Save it once and we'll use it every time.</p>
            <form
                className="flex flex-wrap items-center gap-2"
                onSubmit={(e) => {
                    e.preventDefault();
                    if (valid) save.mutate({ email: email.trim() });
                }}
            >
                <label className="sr-only" htmlFor={`city-email-${jobId}`}>
                    City filing email
                </label>
                <input
                    id={`city-email-${jobId}`}
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="backflow@city.gov"
                    className="min-w-0 flex-1 rounded-lg border border-amber-400 bg-background px-3 py-1.5 text-sm text-foreground"
                />
                <Button type="submit" size="sm" disabled={!valid || save.isPending}>
                    Save for this city
                </Button>
                <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={save.isPending}
                    onClick={() => save.mutate({ mailOnly: true })}
                >
                    They don't take email (mail it)
                </Button>
            </form>
            <p className="text-xs opacity-80">
                You can still send the packet now. The city filing waits until this is set.
            </p>
        </div>
    );
}
